import _ from 'lodash';
import { useQuery, useQueryClient } from '@tanstack/react-query-v4';
import apiFetch, { HTTPError, defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import {
  AllocatedInfo,
  IOnboardingFlow,
  OnboardingFlowNodeType,
  OnboardingFlowType,
} from '@scale/shared/onboardingControlPanel';
import {
  FunnelStepUserData,
  StepUserInfo,
  FunnelStepStats,
  StepUsersResponse,
  StepOverviewDataResponse,
} from '@scale/shared/projectOnboarding';
import { ReviewLevel } from '@scale/shared-types';
import {
  CreateQualificationListDTO,
  QualificationJSON,
  QualificationListJSON,
} from '@scale/shared/types/qualification/types';
import { useEffect, useState } from 'react';
import { UseMutationResult, useMutation } from 'react-query';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import { useCreateQualification } from 'react-scale/hooks/qualifications/mutations/useCreateQualification';
import { useCreateQualificationLists } from 'react-scale/hooks/qualifications/mutations/useCreateQualificationListsForProject';
import { useUpdateQualification } from 'react-scale/hooks/qualifications/mutations/useUpdateQualification';
import { useUpdateQualificationList } from 'react-scale/hooks/qualifications/mutations/useUpdateQualificationList';
import { useFetchQualificationLists } from 'react-scale/hooks/qualifications/queries/useFetchQualificationLists';
import useACPStore from 'react-scale/corp/hooks/qm/acp/useACPStore';

export async function findACPWorkflows(searchQuery?: string) {
  const queryParams = new URLSearchParams();
  if (searchQuery) {
    queryParams.append('searchQuery', searchQuery);
  }
  return apiFetch<IOnboardingFlow[]>(
    null,
    `corp-api/project-onboarding/flow?${queryParams.toString()}`,
    {
      method: 'GET',
    },
  );
}

const useMaybeUpdateACPWorkflows = (
  projectId?: string,
  onSuccess?: () => void,
): UseMutationResult<any> => {
  const qc = useQueryClient();
  return useMutation(
    ['maybeUpdateACPWorkflows', projectId],
    async () => {
      if (projectId) {
        const result = await apiFetch(
          null,
          `corp-api/project-onboarding/project/${projectId}/refreshFlow/sync`,
          {
            method: 'POST',
          },
        );
        return result;
      } else {
        throw new Error('Project ID is required');
      }
    },
    {
      onSuccess: async () => {
        await qc.invalidateQueries({
          queryKey: ['getFlowId', projectId],
          exact: false,
          type: 'all',
        });
        await qc.invalidateQueries({
          queryKey: ['getFlowById'],
          exact: false,
          type: 'all',
        });
        onSuccess && onSuccess();
      },
      onError: (err: HTTPError) => defaultApiFetchErrorHandler(err, { handleHttp: true }),
    },
  );
};

const useMaybeUpdateACPWorkflowsV2 = (
  projectId?: string,
  onSuccess?: () => void,
): UseMutationResult<any> => {
  const { currentFlowId, draftFlows } = useACPStore(state => ({
    currentFlowId: state.currentFlowId,
    draftFlows: state.draftFlows,
  }));
  const qc = useQueryClient();
  return useMutation(
    ['maybeUpdateACPWorkflowsV2', projectId, currentFlowId],
    async () => {
      if (projectId) {
        const body = JSON.stringify({
          draftFlow: draftFlows[currentFlowId],
        });

        const result = await apiFetch(
          null,
          `corp-api/project-onboarding/project/${projectId}/acp/publish`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body,
          },
        );
        return result;
      } else {
        throw new Error('Project ID is required');
      }
    },
    {
      onSuccess: async () => {
        await qc.invalidateQueries({
          queryKey: ['getFlowId', projectId],
          exact: false,
          type: 'all',
        });
        await qc.invalidateQueries({
          queryKey: ['getFlowById'],
          exact: false,
          type: 'all',
        });
        onSuccess && onSuccess();
      },
      onError: (err: HTTPError) => defaultApiFetchErrorHandler(err, { handleHttp: true }),
    },
  );
};

export function useStepOverviewData(flowId: string, stepName: string) {
  return useQuery({
    queryKey: ['stepOverviewData', flowId, stepName],
    queryFn: async () => {
      if (stepName === 'Removed') {
        return null;
      }
      const response = await apiFetch<StepOverviewDataResponse>(
        null,
        `corp-api/project-onboarding/stepOverviewData/${flowId}/${stepName}`,
      );
      return response;
    },
  });
}

export function useSearchUsersInOnboardingFlow(
  onboardingFlowId: string | undefined,
  searchTerm: string,
) {
  return useQuery({
    queryKey: ['searchUsersInOnboardingFlow', onboardingFlowId, searchTerm],
    queryFn: async () => {
      if (!searchTerm || !onboardingFlowId) {
        return [];
      }
      const results = await apiFetch<
        Array<{ userId: string; onboardingStep: string; userEmail: string }>
      >(null, `corp-api/project-onboarding/searchUsers/${onboardingFlowId}?q=${searchTerm}`);
      return results;
    },
  });
}

export function useGetUserInOnboardingFlow(
  onboardingFlowId: string | undefined,
  userId: string | null,
) {
  return useQuery({
    queryKey: ['getUserInOnboardingFlow', onboardingFlowId, userId],
    queryFn: async () => {
      if (!onboardingFlowId || !userId) {
        return null;
      }
      const result = await apiFetch(
        null,
        `corp-api/project-onboarding/user/${userId}/${onboardingFlowId}`,
      );
      return result;
    },
  });
}

// gets all active users for all steps in a flow and returns a mapping
export function useAllStepsActiveUsers(
  flow: IOnboardingFlow | undefined | null,
  type: OnboardingFlowType,
) {
  return useQuery({
    queryKey: ['allStepsActiveUsers', flow?._id, type],
    queryFn: async () => {
      // create a mapping of step name to active users
      if (!flow) return [];

      const promises = flow.nodes.map(node =>
        apiFetch<StepUsersResponse>(null, `corp-api/project-onboarding/step/active-users`, {
          method: 'POST',
          body: {
            flowId: flow._id,
            step: node.name,
          },
        }).then(response => {
          const nodeType = node.type === OnboardingFlowNodeType.Step ? node.stepType : node.type;
          return {
            stepName: node.name,
            stepType: nodeType,
            totalUsers: response.totalUsers,
            directAssignedUsers: response.directAssignedUsers,
            chosenProjectUsers: response.chosenProjectUsers,
            primaryTeamUsers: response.primaryTeamUsers,
            secondaryTeamUsers: response.secondaryTeamUsers,
          };
        }),
      );

      // Extra call for "Removed" step
      promises.push(
        apiFetch<StepUsersResponse>(null, `corp-api/project-onboarding/step/active-users`, {
          method: 'POST',
          body: {
            flowId: flow._id,
            step: 'Removed',
          },
        }).then(response => {
          return {
            stepName: 'Removed',
            stepType: OnboardingFlowNodeType.Terminus,
            totalUsers: response.totalUsers,
            directAssignedUsers: response.directAssignedUsers,
            chosenProjectUsers: response.chosenProjectUsers,
            primaryTeamUsers: response.primaryTeamUsers,
            secondaryTeamUsers: response.secondaryTeamUsers,
          };
        }),
      );
      // TODO: wrap each promise in a retryable wrapper and a promise.map so a single failure doesn't retry everything
      const results = await Promise.all(promises);
      return results;
    },
  });
}

export function useFetchActiveACPUsersOrdered(flowId: string, stepName: string, enabled: boolean) {
  return useQuery({
    queryKey: ['activeACPUsersOrdered', flowId, stepName],
    queryFn: async () => {
      if (!enabled) {
        return null;
      }
      return await apiFetch<{
        sortedUsers: StepUserInfo[];
      }>(null, `corp-api/project-onboarding/activeUsersOnStepData/${flowId}/${stepName}`);
    },
  });
}

export async function downloadUsersOnStepCSV(
  flowId: string,
  flowName: string,
  stepName: string,
): Promise<void> {
  try {
    // Construct the URL with the flowId and stepName
    const url = `/corp-api/project-onboarding/flow/${encodeURIComponent(
      flowId,
    )}/step/${encodeURIComponent(stepName)}/users/csv`;

    const response = await apiFetch<Blob>(null, url, {
      method: 'GET',
      binary: true,
    });

    const downloadUrl = window.URL.createObjectURL(response);

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `${flowName}-${stepName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    alert(`Error downloading CSV: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export function useGetFlowId(projectId: string, reviewLevel: ReviewLevel) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['getFlowId', projectId, reviewLevel],
    queryFn: async () => {
      try {
        return await apiFetch<string>(
          null,
          `corp-api/project-onboarding/flowIdByProjectMapping/${projectId}/${reviewLevel}`,
        );
      } catch (error) {
        defaultApiFetchErrorHandler(error);
        throw new Error('Failed to fetch flowId');
      }
    },
    onSuccess: data => {
      queryClient.setQueryData(['getFlowId', projectId, reviewLevel], data);
    },
  });
}

async function getFlowById(flowId: string, version?: number) {
  const url =
    version !== undefined && typeof version === 'number'
      ? `corp-api/project-onboarding/flow/${flowId}?version=${version}`
      : `corp-api/project-onboarding/flow/${flowId}`;
  return await apiFetch<IOnboardingFlow>(null, url, {
    method: 'GET',
  });
}

export function useGetFlowById(flowId?: string, version?: number) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['getFlowById', flowId, version],
    queryFn: async () => {
      try {
        if (!flowId) {
          return null;
        } else {
          return await getFlowById(flowId, version);
        }
      } catch (error) {
        defaultApiFetchErrorHandler(error);
        throw new Error('Failed to fetch flow');
      }
    },
    onSuccess: data => {
      queryClient.setQueryData(['getFlowById', flowId, version], data);
    },
  });
}

interface InitFlowResponse {
  message: string;
}

export function useInitFlow(projectId: string, userId: string, acpReviewLevel: ReviewLevel) {
  const [triggerQuery, setTriggerQuery] = useState<boolean>(false);
  const query = useQuery({
    queryKey: ['useInitFlow', projectId, userId],
    queryFn: async () =>
      apiFetch<InitFlowResponse>(
        null,
        `corp-api/project-onboarding/project/${projectId}/initFlow`,
        {
          method: 'POST',
          body: JSON.stringify({
            userId,
            reviewLevel: acpReviewLevel,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      ),
    enabled: triggerQuery,
  });

  return { ...query, triggerQuery, setTriggerQuery };
}

export function useNumAllocated(projectId: string, reviewLevel: ReviewLevel) {
  return useQuery({
    queryKey: ['numAllocated', projectId, reviewLevel],
    queryFn: async () => {
      try {
        const response = await apiFetch<AllocatedInfo[]>(
          null,
          `corp-api/project-onboarding/project/${projectId}/allocated/${reviewLevel}`,
          {
            method: 'GET',
          },
        );
        return response;
      } catch (error) {
        defaultApiFetchErrorHandler(error);
        throw new Error('Failed to fetch allocated numbers');
      }
    },
  });
}

type EditACPFlowParams = {
  onSuccess?: () => void;
  updatedQualification: Partial<QualificationJSON>;
};

type EditACPFlowParamsV2 = {
  onSuccess?: () => void;
};

function isCreateQualificationJSON(
  params: Partial<QualificationJSON>,
): params is Omit<QualificationJSON, '_id' | 'id'> {
  return !!(params.displayName && params.name && params.qualificationType);
}

function isUpdateQualificationJSON(
  params: Partial<QualificationJSON>,
): params is Partial<QualificationJSON> & Pick<QualificationJSON, '_id'> {
  return !!params._id;
}

export const useEditACPFlow = (): {
  handleEditACPFlow: (params: EditACPFlowParams) => Promise<void>;
  isSaveDisabled: boolean;
  setHasError: (hasError: boolean) => void;
  hasError: boolean;
  setSelectedQualification: (qualification: QualificationJSON | null) => void;
} => {
  const projectId = useProjectId();
  const [qualificationLists, setQualificationLists] = useState<QualificationListJSON[]>();
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
  const [selectedQualification, setSelectedQualification] = useState<QualificationJSON | null>(
    null,
  );

  const { refetch: loadQualificationLists } = useFetchQualificationLists(
    {
      projectId,
    },
    res => {
      setQualificationLists(res);
    },
  );

  useEffect(() => {
    if (!qualificationLists) {
      loadQualificationLists();
    }
  }, [qualificationLists, loadQualificationLists]);

  const { mutateAsync: createQualification } = useCreateQualification();
  const { mutateAsync: updateQualification } = useUpdateQualification();
  const { mutateAsync: updateQualificationList } = useUpdateQualificationList();
  const { mutateAsync: createQualificationList } = useCreateQualificationLists();
  const { mutateAsync: maybeUpdateACPWorkflows } = useMaybeUpdateACPWorkflows(projectId);

  const handleEditACPFlow = async ({ onSuccess, updatedQualification }: EditACPFlowParams) => {
    try {
      setIsSaveDisabled(true);
      let qualification: QualificationJSON | null = null;
      if (selectedQualification && isUpdateQualificationJSON(updatedQualification)) {
        if (_.isEqual(selectedQualification, updatedQualification)) {
          qualification = selectedQualification;
        } else {
          qualification = await updateQualification(updatedQualification);
        }
      } else if (!selectedQualification && isCreateQualificationJSON(updatedQualification)) {
        qualification = await createQualification(updatedQualification);
      }

      if (qualificationLists && qualificationLists.length > 0) {
        const qualificationList = qualificationLists[0];
        const qualifications = [...(qualificationList?.qualifications || [])];
        const index = qualifications.findIndex(qual => qual._id === qualification?._id);
        if (index !== -1 && qualification) {
          qualifications[index] = qualification;
        } else if (qualification) {
          qualifications.push(qualification);
        }
        await updateQualificationList({
          ...qualificationList,
          qualifications,
        });
      } else {
        await createQualificationList([
          { name: 'acp-qualification-list', qualifications: [qualification] },
        ] as CreateQualificationListDTO[]);
      }

      if (projectId) await maybeUpdateACPWorkflows(projectId);

      onSuccess && onSuccess();
    } catch (error) {
      alert(`Error editing acp flow: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsSaveDisabled(false);
    }
  };

  return {
    hasError,
    handleEditACPFlow,
    isSaveDisabled,
    setHasError,
    setSelectedQualification,
  };
};

export const useEditACPFlowV2 = (): {
  isSaveDisabled: boolean;
  handleEditACPFlow: (params: EditACPFlowParamsV2) => Promise<void>;
} => {
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
  const projectId = useProjectId();

  const { mutateAsync: maybeUpdateACPWorkflowsV2 } = useMaybeUpdateACPWorkflowsV2(projectId);

  const handleEditACPFlow = async ({ onSuccess }: EditACPFlowParamsV2) => {
    try {
      setIsSaveDisabled(true);
      if (!_.isNil(projectId)) {
        await maybeUpdateACPWorkflowsV2(projectId);
      }
      onSuccess && onSuccess();
    } catch (error) {
      alert(`Error editing acp flow: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsSaveDisabled(false);
    }
  };

  return {
    handleEditACPFlow,
    isSaveDisabled,
  };
};

interface FunnelStatsParams {
  onboardingFlowId?: string;
  projectId: string;
  dateRange: Date[];
  chosenProjectFilter: boolean;
  directAssignedFilter: boolean;
  primaryTeamAssignedFilter: boolean;
  secondaryTeamAssignedFilter: boolean;
  allUsersFilter: boolean;
  qualificationListId?: string;
  version?: number;
}

export function useFunnelStats({
  onboardingFlowId,
  projectId,
  dateRange,
  chosenProjectFilter,
  directAssignedFilter,
  allUsersFilter,
  qualificationListId,
  primaryTeamAssignedFilter,
  secondaryTeamAssignedFilter,
  version,
}: FunnelStatsParams) {
  return useQuery({
    queryKey: [
      'funnelStats',
      onboardingFlowId,
      version,
      projectId,
      dateRange,
      chosenProjectFilter,
      directAssignedFilter,
      primaryTeamAssignedFilter,
      secondaryTeamAssignedFilter,
      allUsersFilter,
      qualificationListId,
    ],
    queryFn: async () => {
      const response = await apiFetch<{
        funnelStats: FunnelStepStats[];
        sqlQuery: string;
        flowVersion: number;
      }>(null, `corp-api/project-onboarding/project/${projectId}/funnel-stats`, {
        method: 'POST',
        body: {
          onboardingFlowId,
          version,
          dateRange: {
            startDate: dateRange[0],
            endDate: dateRange[1],
          },
          filters: {
            chosenProjectFilter,
            directAssignedFilter,
            primaryTeamAssignedFilter,
            secondaryTeamAssignedFilter,
            allUsersFilter,
          },
          qualificationListId,
        },
      });
      return response;
    },
    enabled: Boolean(onboardingFlowId && projectId),
  });
}

interface FetchFunnelStepUsersParams {
  projectId: string;
  selectedStep: string | null;
  onboardingFlowId?: string;
  page?: number;
  pageSize?: number;
  sortingId?: string;
  sortingDesc?: boolean;
  userIds?: string[];
  dateRange?: Date[];
}

export function useFetchFunnelStepUsersData({
  projectId,
  selectedStep,
  onboardingFlowId,
  page = 0,
  pageSize = 25,
  sortingId,
  sortingDesc,
  userIds,
  dateRange,
}: FetchFunnelStepUsersParams) {
  const queryClient = useQueryClient();

  // Add useEffect to handle page changes
  useEffect(() => {
    if (!projectId || !selectedStep || !onboardingFlowId) return;

    // Prefetch next two pages
    [1, 2].forEach(offset => {
      const nextPage = page + offset;
      queryClient.prefetchQuery({
        queryKey: [
          'fetchFunnelStepUsers',
          projectId,
          selectedStep,
          onboardingFlowId,
          nextPage,
          pageSize,
          sortingId,
          sortingDesc,
          dateRange,
        ],
        queryFn: () =>
          apiFetch(
            null,
            `corp-api/project-onboarding/project/${projectId}/funnel-step-users-data`,
            {
              method: 'POST',
              body: {
                step: selectedStep,
                onboardingFlowId,
                page: nextPage,
                pageSize,
                sortingId,
                sortingDesc,
                userIds,
              },
            },
          ),
      });
    });
  }, [
    page,
    projectId,
    selectedStep,
    onboardingFlowId,
    pageSize,
    sortingId,
    sortingDesc,
    userIds,
    queryClient,
    dateRange,
  ]);

  // Main query
  return useQuery({
    queryKey: [
      'fetchFunnelStepUsers',
      projectId,
      selectedStep,
      onboardingFlowId,
      page,
      pageSize,
      sortingId,
      sortingDesc,
      dateRange,
    ],
    queryFn: async () => {
      const response = await apiFetch<FunnelStepUserData>(
        null,
        `corp-api/project-onboarding/project/${projectId}/funnel-step-users-data`,
        {
          method: 'POST',
          body: {
            step: selectedStep,
            onboardingFlowId,
            page,
            pageSize,
            sortingId,
            sortingDesc,
            userIds,
          },
        },
      );
      return response;
    },
    enabled: Boolean(projectId && selectedStep && onboardingFlowId),
    staleTime: Infinity,
    cacheTime: 5 * 60 * 1000,
    keepPreviousData: false,
  });
}
